import React from 'react';
import { Result } from 'antd';

import { useCurrentUser } from '../../contexts/CurrentUserContext';
import { hasRole, isAdmin, isTokenValid } from '../../utils';
import RequireSession from '../common/RequireSession';
import { getSessionToken } from '../../utils/session';

const holdsToken = () => Boolean(getSessionToken());

/**
 * Platform-admin route guard. Prefers the reactive roles on the
 * CurrentUserContext user (the /api/user-info private projection includes
 * `roles`), so a role change is honored as soon as user-info refreshes.
 * While user-info is still loading (user === null) it falls back to the
 * JWT-derived isAdmin() so an admin hard-refreshing /admin does not flash
 * a denial before the fetch lands.
 *
 * An EXPIRED token is no session: the person is sent to sign in by the
 * session guard (components/common/RequireSession.js), and neither the
 * loaded user nor the token's own roles are consulted — an expired admin JWT
 * never shows an admin page. On /admin the guard around the protected routes
 * gets there first; the admin half of /docs sits under the public portal, so
 * this is where its expired sessions are caught.
 *
 * Non-admins get a 403 Result, not a redirect — and so does a visitor with
 * no token at all (the /docs admin pages answer a signed-out visitor with
 * this page; /admin never gets here without a session).
 */
export default function RequireAdmin({ children, allowReviewer = false }) {
	const { user } = useCurrentUser();

	if (holdsToken() && !isTokenValid()) return <RequireSession>{children}</RequireSession>;

	// `allowReviewer` admits the 'reviewer' role too — for the portal shell,
	// which then shows a reviewer the Review section alone.
	const roles = user ? (Array.isArray(user.roles) ? user.roles : []) : null;
	const allowed = roles
		? roles.includes('admin') || (allowReviewer && roles.includes('reviewer'))
		: isAdmin() || (allowReviewer && hasRole('reviewer'));

	if (!allowed) {
		return (
			<Result
				status='403'
				title='403'
				subTitle='You need administrator access to view this page.'
			/>
		);
	}

	return <>{children}</>;
}
